import React, { useCallback, useEffect } from 'react'
import { selectResetPassword } from 'selectors/auth'
import { useForm } from 'components/utils/useForm'
import { resetPassword as resetPasswordAction, dismissResetPasswordError } from 'actions/auth'
import Button from 'components/utils/Button'
import FormGroup from 'components/utils/FormGroup'
import FormLabel from 'components/utils/FormLabel'
import FormControl from 'components/utils/FormControl'
import FormHelp from 'components/utils/FormHelp'
import Alert from 'components/utils/Alert'
import { Grid, Row, Col } from 'components/utils/Grid'
import { useAppSelector, useAppDispatch } from 'store/hooks'

const validators = {
  resetToken: {
    errors: {
      'Enter your Reset Token': (resetToken: string) => resetToken.length === 0,
    },
  },
  password: {
    errors: {
      'Choose a password': (password: string) => password.length === 0,
      'Your password should be at least 8 characters long': (password: string) => password.length < 8,
    },
  },
  confirmPassword: {
    errors: {
      "Passwords don't match": (password: string, state: { fields: Record<string, { value: string }> }) =>
        password !== state.fields.password?.value,
    },
  },
}

const ResetPasswordForm = () => {
  const { resetPassword, errors } = useAppSelector(selectResetPassword)
  const dispatch = useAppDispatch()
  const rp = (resetPassword || {}) as { isFetching?: boolean; resetToken?: string }

  const onSubmit = useCallback(
    (values: Record<string, string>) => {
      Promise.resolve(dispatch(resetPasswordAction(values as any) as any)).then(
        (a: { error?: unknown }) => {
          if (!a.error) {
            window.location.href = '/login'
          }
        }
      )
    },
    [dispatch]
  )

  const { handleFieldChange, handleSubmit, fieldValue, fieldMessage, validationState, formInvalid, setFormState } =
    useForm({
      initialFields: { password: '', confirmPassword: '', resetToken: '' },
      validators,
      onSubmit,
    })

  useEffect(() => {
    if (rp.resetToken) {
      setFormState((prev) => ({
        ...prev,
        fields: {
          ...prev.fields,
          resetToken: { value: rp.resetToken!, state: null, valid: true, message: '' },
        },
      }))
    }
  }, [rp.resetToken, setFormState])

  const buttonText = rp.isFetching ? 'Resetting your password...' : 'Reset Password'
  const buttonDisabled = formInvalid || rp.isFetching

  const renderErrors = () => {
    const errs = (errors as unknown[]) || []
    if (!(errs.length > 0)) {
      return null
    }
    return (
      <div>
        {errs.map((error: unknown, idx: number) => (
          <Alert key={idx} onDismiss={() => dispatch(dismissResetPasswordError(idx))}>
            {String(error)}
          </Alert>
        ))}
      </div>
    )
  }

  return (
    <Grid>
      <Row>
        <Col sm={5} smPush={1}>
          <h2>Reset Password</h2>
          <p>Enter the reset token sent to you in an email</p>
          <form onSubmit={handleSubmit}>
            <FormGroup error={validationState('resetToken') === 'error'}>
              <FormLabel>Reset token</FormLabel>
              <FormControl
                required
                type="text"
                value={fieldValue('resetToken')}
                placeholder="reset token..."
                onChange={handleFieldChange.bind(null, 'resetToken')}
              />
              <FormHelp>{fieldMessage('resetToken')}</FormHelp>
            </FormGroup>
            <FormGroup error={validationState('password') === 'error'}>
              <FormLabel>New Password</FormLabel>
              <FormControl
                required
                type="password"
                value={fieldValue('password')}
                placeholder="new password..."
                onChange={handleFieldChange.bind(null, 'password')}
              />
              <FormHelp>{fieldMessage('password')}</FormHelp>
            </FormGroup>
            <FormGroup error={validationState('confirmPassword') === 'error'}>
              <FormLabel>Confirm password</FormLabel>
              <FormControl
                required
                type="password"
                value={fieldValue('confirmPassword')}
                placeholder="password again..."
                onChange={handleFieldChange.bind(null, 'confirmPassword')}
              />
              <FormHelp>{fieldMessage('confirmPassword')}</FormHelp>
            </FormGroup>
            <Button type="submit" disabled={buttonDisabled}>
              {buttonText}
            </Button>
          </form>
          {renderErrors()}
        </Col>
      </Row>
    </Grid>
  )
}

export default ResetPasswordForm
